import * as React from "react";
import { useCallback } from "react";
import clsx from "clsx";
import { Checkbox, FormGroup, FormControlLabel, FormHelperText, Switch, type SxProps } from "@mui/material";
import { CommonInputProps, FieldTitle, InputHelperText, sanitizeInputRestProps, useInput } from "react-admin";
import { Typography } from "@mui/material";

type BooleanInputProps = Omit<CommonInputProps, "variant"> & {
  className?: string;
  variant?: "switch" | "checkbox";
  size?: "small" | "medium";
  sx?: SxProps;
};

/** Adapted from BooleanInput in ra-ui-materialui */
export const BooleanInput = (props: BooleanInputProps) => {
  const {
    variant,
    className, // oxlint-disable-line no-unused-vars
    defaultValue = false,
    format,
    label,
    fullWidth, // oxlint-disable-line no-unused-vars
    helperText,
    onBlur,
    onChange,
    readOnly,
    disabled,
    parse,
    resource,
    source,
    validate,
    size,
    sx,
    ...rest
  } = props;
  const {
    id,
    field,
    isRequired,
    fieldState: { error, invalid, isTouched },
    formState: { isSubmitted },
  } = useInput({
    defaultValue,
    format,
    parse,
    resource,
    source,
    onBlur,
    onChange,
    type: "checkbox",
    validate,
    ...rest,
  });

  const handleChange = useCallback(
    (event: any) => {
      field.onChange(event);
      // Ensure field is considered as touched
      field.onBlur();
    },
    [field],
  );

  const renderHelperText = helperText !== false || ((isTouched || isSubmitted) && invalid);

  return (
    <FormGroup className={clsx("ra-input", `ra-input-${source}`)} sx={sx}>
      <FormControlLabel
        inputRef={field.ref}
        control={
          variant === "checkbox" ? (
            <Checkbox
              id={id}
              name={field.name}
              size={size}
              onChange={handleChange}
              checked={Boolean(field.value)}
              {...sanitizeInputRestProps(rest)}
              disabled={disabled || readOnly}
              readOnly={readOnly}
            />
          ) : (
            <Switch
              id={id}
              name={field.name}
              size={size}
              onChange={handleChange}
              checked={Boolean(field.value)}
              {...sanitizeInputRestProps(rest)}
              disabled={disabled || readOnly}
              readOnly={readOnly}
            />
          )
        }
        label={
          <Typography variant={size === "small" ? "body2" : "body1"} sx={{ whiteSpace: "nowrap" }}>
            <FieldTitle label={label} source={source} resource={resource} isRequired={isRequired} />
          </Typography>
        }
      />
      {renderHelperText ? (
        <FormHelperText error={(isTouched || isSubmitted) && invalid}>
          <InputHelperText touched={isTouched || isSubmitted} error={error?.message} helperText={helperText} />
        </FormHelperText>
      ) : null}
    </FormGroup>
  );
};
